import { useState, useCallback, useRef, useEffect } from 'react';
import useLocale from './useLocale';
import { getSpeechAdapter } from 'utils/speech/SpeechAdapterFactory';

// ==============================|| LOCALE TO SPEECH LANGUAGE MAPPING ||============================== //

const LOCALE_TO_SPEECH_LANG = {
  en: 'en-IN',
  hi: 'hi-IN',
  kn: 'kn-IN',
  ta: 'ta-IN',
  ml: 'ml-IN',
  fr: 'fr-FR',
  zh: 'zh-CN',
  ro: 'ro-RO'
};

// ==============================|| HOOK - USE SPEECH TO TEXT ||============================== //
/**
 * Hook for speech-to-text with adapter pattern.
 * Automatically maps the app's current locale to the correct speech language.
 *
 * Uses a callback approach: each final result is delivered via onFinalResult
 * so the consumer can append it directly — no state diffing needed.
 *
 * @param {object} [options]
 * @param {string} [options.provider] - Force a specific provider ('web' | 'azure')
 * @param {string} [options.lang] - Override the speech language (BCP-47 code)
 * @param {function} [options.onFinalResult] - Called with the final transcript text each time a sentence is recognized
 * @returns {{ isListening, isSupported, interimTranscript, error, startListening, stopListening }}
 */
export default function useSpeechToText(options = {}) {
  const { i18n } = useLocale();
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const adapterRef = useRef(null);
  const onFinalResultRef = useRef(options.onFinalResult);

  // Keep callback ref up to date without re-creating startListening
  onFinalResultRef.current = options.onFinalResult;

  // Get adapter once
  if (!adapterRef.current) {
    adapterRef.current = getSpeechAdapter(options.provider);
  }

  const isSupported = !!adapterRef.current?.isSupported();

  const startListening = useCallback(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;

    setError(null);
    setInterimTranscript('');

    const lang = options.lang || LOCALE_TO_SPEECH_LANG[i18n] || 'en-IN';

    adapter.start({
      lang,
      continuous: true,
      interimResults: true,
      onStart: () => setIsListening(true),
      onResult: ({ transcript: text, isFinal }) => {
        if (isFinal) {
          onFinalResultRef.current?.(text);
          setInterimTranscript('');
        } else {
          setInterimTranscript(text);
        }
      },
      onError: (err) => {
        setError(err.message);
        setIsListening(false);
      },
      onEnd: () => {
        setIsListening(false);
        setInterimTranscript('');
      }
    });
  }, [i18n, options.lang, options.provider]);

  const stopListening = useCallback(() => {
    adapterRef.current?.stop();
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      adapterRef.current?.abort();
    };
  }, []);

  return {
    isListening,
    isSupported,
    interimTranscript,
    error,
    startListening,
    stopListening
  };
}
